import React from 'react';
import { ArrowRight, TrendingUp, Target } from 'lucide-react';
import { useCricketFormat } from '@/hooks/useUnitPreferences';

interface DistanceProjectionProps {
  actualDistance: number;
  optimalDistance: number;
  maxHeight: number;
  optimalMaxHeight: number;
  potentialGain: number;
}

export const DistanceProjection: React.FC<DistanceProjectionProps> = ({
  actualDistance,
  optimalDistance,
  maxHeight,
  optimalMaxHeight,
  potentialGain
}) => {
  const { formatDistance } = useCricketFormat();
  
  // Fallback function if formatting fails
  const safeFormatDistance = (value: number, includeUnit: boolean = true) => {
    try {
      return formatDistance(value, includeUnit);
    } catch (error) {
      console.warn('Unit formatting error:', error);
      return includeUnit ? `${value.toFixed(1)}m` : value.toFixed(1);
    }
  };
  // Create a visual representation of the field
  const scale = 2; // Scale factor for visualization

  const getDistanceCategory = (distance: number) => {
    if (distance >= 60) return { label: 'Six', color: '#22c55e', icon: '6️⃣' };
    if (distance >= 40) return { label: 'Four', color: '#3b82f6', icon: '4️⃣' };
    if (distance >= 25) return { label: 'Good Shot', color: '#f59e0b', icon: '🏏' };
    return { label: 'Defensive', color: '#ef4444', icon: '🛡️' };
  };

  const actualCategory = getDistanceCategory(actualDistance);
  const optimalCategory = getDistanceCategory(optimalDistance);

  return (
    <div className="space-y-6">
      {/* Distance Comparison Header */}
      <div className="flex items-center justify-between">
        <div className="text-center">
          <div className="text-3xl font-bold" style={{ color: actualCategory.color }}>
            {safeFormatDistance(actualDistance)}
          </div>
          <div className="text-sm text-gray-600">Actual Distance</div>
          <div className="text-lg">{actualCategory.icon}</div>
        </div>
        
        <ArrowRight className="h-6 w-6 text-gray-400" />
        
        <div className="text-center">
          <div className="text-3xl font-bold text-green-600">
            {safeFormatDistance(optimalDistance)}
          </div>
          <div className="text-sm text-gray-600">Optimal Distance</div>
          <div className="text-lg">{optimalCategory.icon}</div>
        </div>
        
        <div className="text-center">
          <div className={`text-2xl font-bold ${potentialGain > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
            {potentialGain > 0 ? '+' : ''}{safeFormatDistance(Math.abs(potentialGain))}
          </div>
          <div className="text-sm text-gray-600">Potential Gain</div>
          {potentialGain > 0 ? (
            <TrendingUp className="h-5 w-5 text-amber-600 mx-auto mt-1" />
          ) : (
            <Target className="h-5 w-5 text-gray-400 mx-auto mt-1" />
          )}
        </div>
      </div>

      {/* Field Visualization */}
      <div className="mx-auto max-w-md">
        <svg width="100%" height="200" viewBox="0 0 300 200" className="border rounded-lg bg-gradient-to-b from-sky-100 to-green-100">
          {/* Field boundary */}
          <circle cx="150" cy="190" r="140" fill="none" stroke="#22c55e" strokeWidth="2" strokeDasharray="5,5" />
          
          {/* 30-yard circle */}
          <circle cx="150" cy="190" r="70" fill="none" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
          
          {/* Pitch */}
          <rect x="140" y="170" width="20" height="40" fill="#d2b48c" stroke="#8b4513" strokeWidth="1" />
          
          {/* Batsman position */}
          <circle cx="150" cy="185" r="3" fill="#ef4444" />
          
          {/* Actual trajectory arc */}
          <path
            d={`M 150 185 Q ${150 + (actualDistance * Math.cos(Math.PI/6))} ${185 - maxHeight * 2} ${150 + actualDistance * scale} ${185 - 5}`}
            fill="none"
            stroke={actualCategory.color}
            strokeWidth="3"
            opacity="0.8"
          />
          
          {/* Optimal trajectory arc */}
          <path
            d={`M 150 185 Q ${150 + (optimalDistance * Math.cos(Math.PI/6))} ${185 - optimalMaxHeight * 2} ${150 + optimalDistance * scale} ${185 - 5}`}
            fill="none"
            stroke="#22c55e"
            strokeWidth="2"
            strokeDasharray="5,5"
            opacity="0.7"
          />
          
          {/* Landing points */}
          <circle 
            cx={150 + actualDistance * scale} 
            cy={185 - 5} 
            r="4" 
            fill={actualCategory.color} 
            stroke="#ffffff" 
            strokeWidth="1"
          />
          <circle 
            cx={150 + optimalDistance * scale} 
            cy={185 - 5} 
            r="4" 
            fill="#22c55e" 
            stroke="#ffffff" 
            strokeWidth="1"
          />
          
          {/* Distance markers */}
          <text x={150 + actualDistance * scale} y={200} fontSize="10" textAnchor="middle" className="font-mono">
            {safeFormatDistance(actualDistance, false)}
          </text>
          <text x={150 + optimalDistance * scale} y={175} fontSize="10" textAnchor="middle" className="font-mono text-green-600">
            {safeFormatDistance(optimalDistance, false)}
          </text>
          
          {/* Field markers */}
          <text x="80" y="130" fontSize="8" textAnchor="middle" className="fill-gray-600">30m</text>
          <text x="50" y="90" fontSize="8" textAnchor="middle" className="fill-green-600">Boundary</text>
          
          {/* Legend */}
          <g transform="translate(10, 10)">
            <text fontSize="8" className="fill-gray-700 font-semibold">Trajectory:</text>
            <line x1="0" y1="15" x2="15" y2="15" stroke={actualCategory.color} strokeWidth="2" />
            <text x="20" y="18" fontSize="7" className="fill-gray-600">Actual</text>
            <line x1="0" y1="25" x2="15" y2="25" stroke="#22c55e" strokeWidth="2" strokeDasharray="3,3" />
            <text x="20" y="28" fontSize="7" className="fill-gray-600">Optimal</text>
          </g>
        </svg>
      </div>

      {/* Height Comparison */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-600">
            {safeFormatDistance(maxHeight)}
          </div>
          <div className="text-sm text-gray-600">Actual Height</div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">
            {safeFormatDistance(optimalMaxHeight)}
          </div>
          <div className="text-sm text-gray-600">Optimal Height</div>
        </div>
      </div>

      {/* Performance Analysis */}
      <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Target className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-gray-900">Performance Analysis</span>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Shot Classification:</span>
            <span className="font-medium" style={{ color: actualCategory.color }}>
              {actualCategory.label}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Distance Efficiency:</span>
            <span className="font-medium">
              {Math.abs(potentialGain) < 0.1 ? '100.0' : ((actualDistance / optimalDistance) * 100).toFixed(1)}%
            </span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Boundary Status:</span>
            <span className={`font-medium ${
              actualDistance >= 60 ? 'text-green-600' : 
              actualDistance >= 40 ? 'text-blue-600' : 
              'text-gray-600'
            }`}>
              {actualDistance >= 60 ? 'Six (Over boundary)' :
               actualDistance >= 40 ? 'Four (To boundary)' :
               actualDistance >= 25 ? 'Safe runs' :
               'Defensive shot'}
            </span>
          </div>
          
          {potentialGain > 10 && (
            <div className="mt-3 p-2 bg-amber-100 rounded text-amber-800">
              <span className="font-medium">Significant improvement potential!</span>
              <br />
              Optimizing technique could advance from {actualCategory.label} to {optimalCategory.label}
            </div>
          )}
          
          {Math.abs(potentialGain) < 0.1 && (
            <div className="mt-3 p-2 bg-green-100 rounded text-green-800">
              <span className="font-medium">Excellent shot execution!</span>
              <br />
              This shot achieved optimal distance for the given technique.
            </div>
          )}
        </div>
      </div>

      {/* Distance Breakdown */}
      <div className="grid grid-cols-3 gap-3 text-center text-xs">
        <div className="p-2 bg-gray-50 rounded">
          <div className="font-medium">Carry</div>
          <div className="text-gray-600">{safeFormatDistance(actualDistance * 0.85)}</div>
        </div>
        <div className="p-2 bg-gray-50 rounded">
          <div className="font-medium">Roll</div>
          <div className="text-gray-600">{safeFormatDistance(actualDistance * 0.15)}</div>
        </div>
        <div className="p-2 bg-gray-50 rounded">
          <div className="font-medium">Total</div>
          <div className="text-gray-600">{safeFormatDistance(actualDistance)}</div>
        </div>
      </div>
    </div>
  );
};